import { useState, useEffect, useRef, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  X, Sparkles, ChevronDown, Check, Plus, Loader2, Link, ExternalLink, Search,
} from 'lucide-react'
import { SIZE_CATEGORIES } from './SizePickerModal'
import { BrandKit } from '../../types/brandKit'
import { brandKitApi } from '../../lib/api/brandKits'

export interface AICreativePayload {
  query: string
  content_size: string[]
  brand_kit_id?: number
  reference_images?: File[]
  destination_url?: string
}

interface Props {
  isOpen: boolean
  onClose: () => void
  onGenerate: (payload: AICreativePayload) => void
  isGenerating?: boolean
}

interface SizePreset {
  size: string
  label: string
  category: string
  w: number
  h: number
}

const PRESET_SIZES: SizePreset[] = (() => {
  const seen = new Set<string>()
  const list: SizePreset[] = []
  for (const cat of SIZE_CATEGORIES) {
    for (const s of cat.sizes) {
      if (seen.has(s.dimensions)) continue
      const m = s.dimensions.match(/^(\d+)x(\d+)$/i)
      if (!m) continue
      seen.add(s.dimensions)
      list.push({
        size: s.dimensions,
        label: s.label,
        category: cat.label,
        w: parseInt(m[1], 10),
        h: parseInt(m[2], 10),
      })
    }
  }
  return list
})()

function getPresetBySize(size: string): SizePreset | undefined {
  return PRESET_SIZES.find(p => p.size === size)
}

export function AICreativeModal({ isOpen, onClose, onGenerate, isGenerating = false }: Props) {
  const [query, setQuery] = useState('')
  const [selectedSizes, setSelectedSizes] = useState<Set<string>>(new Set())
  const [referenceImages, setReferenceImages] = useState<File[]>([])
  const [imagePreviews, setImagePreviews] = useState<string[]>([])
  const [brandKits, setBrandKits] = useState<BrandKit[]>([])
  const [selectedBrandKitId, setSelectedBrandKitId] = useState<number | undefined>()
  const [destinationUrl, setDestinationUrl] = useState('')
  // Only surface URL validation errors after the user has interacted with
  // the field (or attempted submit) so the modal doesn't show errors as
  // soon as it opens.
  const [destinationUrlTouched, setDestinationUrlTouched] = useState(false)
  // Ref to the URL input so we can scroll it into view + focus it when the
  // user clicks the still-disabled CTA without ever scrolling down to fill
  // it in (the right column is scrollable on shorter viewports).
  const destinationUrlRef = useRef<HTMLInputElement>(null)
  // Touched flag + ref for the Sizes panel so we can drive the same
  // attempt-to-submit error UX (red border + inline message + smooth
  // scroll into view) we already have for the URL field.
  const [sizesTouched, setSizesTouched] = useState(false)
  const sizesPanelRef = useRef<HTMLDivElement>(null)
  const [brandKitOpen, setBrandKitOpen] = useState(false)
  const [brandKitSearch, setBrandKitSearch] = useState('')
  const [brandKitDropUp, setBrandKitDropUp] = useState(false)
  const [isDraggingFiles, setIsDraggingFiles] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null!)
  const bkDropdownRef = useRef<HTMLDivElement>(null!)
  const bkButtonRef = useRef<HTMLButtonElement>(null!)

  const phase = isGenerating ? 2 : 1

  useEffect(() => {
    if (isOpen) {
      setQuery('')
      setSelectedSizes(new Set())
      setReferenceImages([])
      imagePreviews.forEach(u => URL.revokeObjectURL(u))
      setImagePreviews([])
      setSelectedBrandKitId(undefined)
      setDestinationUrl('')
      setDestinationUrlTouched(false)
      setSizesTouched(false)
      setBrandKitOpen(false)
      setBrandKitDropUp(false)
      setBrandKitSearch('')
      setIsDraggingFiles(false)
      brandKitApi.getBrandKits({ per_page: 50 }).then(r => setBrandKits(r.brand_kits)).catch(() => {})
    }
  }, [isOpen]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    return () => { imagePreviews.forEach(u => URL.revokeObjectURL(u)) }
  }, [imagePreviews])

  useEffect(() => {
    if (!brandKitOpen) return
    const handler = (e: MouseEvent) => {
      if (bkDropdownRef.current && !bkDropdownRef.current.contains(e.target as Node)) setBrandKitOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [brandKitOpen])

  const toggleSize = (size: string) => {
    setSelectedSizes(prev => {
      const next = new Set(prev)
      if (next.has(size)) next.delete(size)
      else next.add(size)
      return next
    })
  }
  const addSize = (size: string) => setSelectedSizes(prev => { const n = new Set(prev); n.add(size); return n })
  const removeSize = (size: string) => setSelectedSizes(prev => { const n = new Set(prev); n.delete(size); return n })
  const addSizesMany = (list: string[]) => setSelectedSizes(prev => { const n = new Set(prev); list.forEach(s => n.add(s)); return n })
  const clearSizes = () => setSelectedSizes(new Set())

  const handleFiles = (files: FileList | File[]) => {
    const arr = Array.from(files).filter(f => f.type.startsWith('image/'))
    if (!arr.length) return
    setReferenceImages(prev => [...prev, ...arr])
    setImagePreviews(prev => [...prev, ...arr.map(f => URL.createObjectURL(f))])
  }

  const removeImage = (idx: number) => {
    URL.revokeObjectURL(imagePreviews[idx])
    setReferenceImages(prev => prev.filter((_, i) => i !== idx))
    setImagePreviews(prev => prev.filter((_, i) => i !== idx))
  }

  /**
   * Landing page URL is required. We accept any string that parses as a
   * URL with an http(s) scheme; we don't enforce a specific TLD or path
   * shape so users can target staging/intranet hosts as well.
   */
  const trimmedUrl = destinationUrl.trim()
  const isDestinationUrlValid = (() => {
    if (!trimmedUrl) return false
    try {
      const u = new URL(trimmedUrl)
      return u.protocol === 'http:' || u.protocol === 'https:'
    } catch {
      return false
    }
  })()

  const handleSubmit = () => {
    if (selectedSizes.size === 0) return
    if (!isDestinationUrlValid) return
    onGenerate({
      query: query.trim() || 'Start creating/generating creative or ad HTML5 template',
      content_size: Array.from(selectedSizes),
      brand_kit_id: selectedBrandKitId,
      reference_images: referenceImages.length > 0 ? referenceImages : undefined,
      destination_url: trimmedUrl,
    })
  }

  const selectedBrandKit = brandKits.find(b => b.id === selectedBrandKitId)
  const filteredBrandKits = brandKits.filter(b => b.name.toLowerCase().includes(brandKitSearch.toLowerCase()))

  const canSubmit = selectedSizes.size > 0 && isDestinationUrlValid && !isGenerating

  const hintMessage = canSubmit
    ? ''
    : selectedSizes.size === 0
    ? 'Pick at least one size'
    : !trimmedUrl
    ? 'Add a landing page URL'
    : !isDestinationUrlValid
    ? 'Enter a valid URL (https://example.com)'
    : ''

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 dark:bg-black/80 backdrop-blur-sm p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.97 }}
            transition={{ duration: 0.15 }}
            onClick={e => e.stopPropagation()}
            className={`bg-white dark:bg-gray-950 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-800 text-gray-900 dark:text-white flex flex-col overflow-hidden ${
              phase === 2 ? 'w-full max-w-md' : 'w-full max-w-6xl h-[90vh]'
            }`}
            layout
          >
            {/* ── Hero header ── */}
            <div className="relative flex-shrink-0 px-8 pt-6 pb-5 border-b border-gray-200 dark:border-gray-800 bg-gradient-to-b from-primary-100 via-white to-white dark:from-primary-900/40 dark:via-gray-950 dark:to-gray-950">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="text-[11px] font-semibold tracking-[0.18em] text-primary-600 dark:text-primary-400 uppercase flex items-center gap-1.5">
                    <Sparkles className="h-3 w-3" />
                    {isGenerating ? 'Generating' : 'New creative'}
                  </div>
                  <h2 className="text-2xl font-bold mt-1">
                    {isGenerating ? 'Generating your creative' : 'Create with AI'}
                  </h2>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                    {isGenerating
                      ? 'Hold tight — this typically takes under a minute.'
                      : 'Describe what you want, pick sizes, and we\u2019ll generate it for you.'}
                  </p>
                </div>
                <button
                  onClick={onClose}
                  aria-label="Close"
                  title="Close"
                  className="h-9 w-9 flex-shrink-0 rounded-full border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/80 hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white flex items-center justify-center transition-colors shadow-sm"
                >
                  <X className="h-4 w-4" strokeWidth={2.5} />
                </button>
              </div>
            </div>

            {/* ── Phase 1: Input ── */}
            {phase === 1 && (
              <>
                <div className="flex-1 min-h-0 grid grid-cols-1 md:grid-cols-[1fr_420px]">
                  {/* LEFT: Prompt + Reference Assets */}
                  <div className="flex flex-col p-6 gap-5 min-w-0 border-r border-gray-200 dark:border-gray-800 overflow-y-auto scrollbar-thin">
                    <div>
                      <label className="block text-[11px] font-semibold tracking-wider uppercase text-gray-600 dark:text-gray-400 mb-1.5">
                        Prompt
                      </label>
                      <textarea
                        value={query}
                        onChange={e => setQuery(e.target.value)}
                        placeholder="Describe your ad creative — e.g. 'Summer sale banner for fashion brand with vibrant colours and bold typography'"
                        className="w-full rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900/60 text-sm px-4 py-3 resize-none focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 h-[120px] scrollbar-thin"
                      />
                    </div>

                    <div className="flex-1 min-h-0 flex flex-col">
                      <div className="flex items-center justify-between mb-1.5">
                        <label className="text-[11px] font-semibold tracking-wider uppercase text-gray-600 dark:text-gray-400">
                          Reference Assets
                        </label>
                        {imagePreviews.length > 0 && (
                          <span className="text-[11px] text-gray-500">{imagePreviews.length} file{imagePreviews.length !== 1 ? 's' : ''}</span>
                        )}
                      </div>
                      <div
                        onDragOver={e => { e.preventDefault(); setIsDraggingFiles(true) }}
                        onDragLeave={() => setIsDraggingFiles(false)}
                        onDrop={e => { e.preventDefault(); setIsDraggingFiles(false); handleFiles(e.dataTransfer.files) }}
                        className={`flex-1 rounded-xl border-2 border-dashed p-4 transition-colors ${
                          isDraggingFiles
                            ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                            : 'border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/30'
                        }`}
                      >
                        <div className="flex flex-wrap gap-3 content-start">
                          {imagePreviews.map((url, i) => (
                            <div key={i} className="relative group w-16 h-16">
                              <img src={url} alt="" className="w-full h-full object-cover rounded-lg border border-gray-200 dark:border-gray-700" />
                              <button
                                onClick={() => removeImage(i)}
                                className="absolute -top-1.5 -right-1.5 h-4 w-4 rounded-full bg-gray-500 dark:bg-gray-700 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <X className="h-2.5 w-2.5" />
                              </button>
                            </div>
                          ))}
                          <button
                            onClick={() => fileInputRef.current?.click()}
                            className="w-16 h-16 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-700 flex items-center justify-center text-gray-400 dark:text-gray-500 hover:border-primary-500 hover:text-primary-500 dark:hover:text-primary-400 transition-colors"
                          >
                            <Plus className="h-5 w-5" />
                          </button>
                        </div>
                        {imagePreviews.length === 0 && (
                          <p className="mt-3 text-[11px] text-gray-500">
                            Drag &amp; drop images here or click + to browse.
                          </p>
                        )}
                      </div>
                      <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden"
                        onChange={e => e.target.files && handleFiles(e.target.files)} />
                    </div>
                  </div>

                  {/* RIGHT: Landing URL + Sizes + Brand Kit. Landing URL
                      moved to the top so the user always sees the required
                      field before scrolling — previously it was hidden
                      below the tall Sizes panel and the Generate button
                      stayed disabled with no obvious reason. */}
                  <div className="flex flex-col min-w-0 overflow-y-auto scrollbar-thin">
                    <div className="p-6 space-y-5">
                      <div>
                        <label className="block text-[11px] font-semibold tracking-wider uppercase text-gray-600 dark:text-gray-400 mb-1.5 flex items-center gap-1">
                          <Link className="h-3 w-3" /> Landing Page URL
                          <span className="text-red-500 ml-0.5" aria-hidden="true">*</span>
                        </label>
                        <div className="relative">
                          <input
                            ref={destinationUrlRef}
                            type="url"
                            required
                            aria-required="true"
                            aria-invalid={destinationUrlTouched && !isDestinationUrlValid}
                            value={destinationUrl}
                            onChange={e => setDestinationUrl(e.target.value)}
                            onBlur={() => setDestinationUrlTouched(true)}
                            placeholder="https://example.com"
                            className={`w-full rounded-lg border bg-white dark:bg-gray-900/60 text-sm pl-3 pr-8 py-2 focus:outline-none focus:ring-2 focus:border-transparent text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 ${
                              destinationUrlTouched && !isDestinationUrlValid
                                ? 'border-red-400 dark:border-red-500/70 focus:ring-red-500/40'
                                : 'border-gray-200 dark:border-gray-800 focus:ring-primary-500'
                            }`}
                          />
                          {destinationUrl && <ExternalLink className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-500" />}
                        </div>
                        {destinationUrlTouched && !isDestinationUrlValid && (
                          <p className="mt-1 text-[11px] text-red-500 dark:text-red-400">
                            {trimmedUrl
                              ? 'Enter a valid URL (must start with http:// or https://).'
                              : 'Landing page URL is required.'}
                          </p>
                        )}
                      </div>

                      <div ref={sizesPanelRef}>
                        <SizesPanel
                          sizes={selectedSizes}
                          onToggle={toggleSize}
                          onAdd={addSize}
                          onRemove={removeSize}
                          onAddMany={addSizesMany}
                          onClearAll={clearSizes}
                          showRequiredError={sizesTouched && selectedSizes.size === 0}
                        />
                      </div>

                      <div ref={bkDropdownRef} className="relative">
                        <label className="block text-[11px] font-semibold tracking-wider uppercase text-gray-600 dark:text-gray-400 mb-1.5">
                          Brand Kit
                        </label>
                        <button
                          ref={bkButtonRef}
                          onClick={() => {
                            if (!brandKitOpen) {
                              const r = bkButtonRef.current?.getBoundingClientRect()
                              if (r) {
                                const dropdownEst = 280
                                const spaceBelow = window.innerHeight - r.bottom
                                const spaceAbove = r.top
                                setBrandKitDropUp(spaceBelow < dropdownEst && spaceAbove > spaceBelow)
                              }
                            }
                            setBrandKitOpen(!brandKitOpen)
                          }}
                          className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg border bg-white dark:bg-gray-900/60 text-sm text-left transition-colors ${
                            brandKitOpen ? 'border-primary-500 ring-2 ring-primary-500/40' : 'border-gray-200 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700'
                          }`}
                        >
                          {selectedBrandKit ? (
                            <>
                              <span className="w-5 h-5 rounded bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-400 flex items-center justify-center text-[10px] font-bold flex-shrink-0">
                                {selectedBrandKit.name.charAt(0).toUpperCase()}
                              </span>
                              <span className="text-gray-900 dark:text-white truncate">{selectedBrandKit.name}</span>
                            </>
                          ) : (
                            <>
                              <span className="w-5 h-5 rounded bg-gray-100 dark:bg-gray-800 flex items-center justify-center flex-shrink-0">
                                <Sparkles className="h-3 w-3 text-gray-500" />
                              </span>
                              <span className="text-gray-400 dark:text-gray-500">Select brand kit</span>
                            </>
                          )}
                          <ChevronDown className={`h-3.5 w-3.5 text-gray-500 ml-auto flex-shrink-0 transition-transform ${brandKitOpen ? 'rotate-180' : ''}`} />
                        </button>
                        <AnimatePresence>
                          {brandKitOpen && (
                            <motion.div
                              initial={{ opacity: 0, y: brandKitDropUp ? 4 : -4 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: brandKitDropUp ? 4 : -4 }}
                              transition={{ duration: 0.12 }}
                              className={`absolute left-0 right-0 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg shadow-2xl z-30 overflow-hidden ${
                                brandKitDropUp ? 'bottom-full mb-1' : 'top-full mt-1'
                              }`}
                            >
                              <div className="p-2">
                                <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-md border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-950 focus-within:border-primary-500 focus-within:ring-2 focus-within:ring-primary-500/40">
                                  <Search className="h-3 w-3 text-gray-500 flex-shrink-0" />
                                  <input
                                    value={brandKitSearch}
                                    onChange={e => setBrandKitSearch(e.target.value)}
                                    placeholder="Search brand kits..."
                                    className="flex-1 min-w-0 bg-transparent text-xs text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 outline-none"
                                    autoFocus
                                  />
                                </div>
                              </div>
                              <div className="max-h-[200px] overflow-y-auto scrollbar-thin">
                                {selectedBrandKitId && (
                                  <button onClick={() => { setSelectedBrandKitId(undefined); setBrandKitOpen(false); setBrandKitSearch('') }}
                                    className="w-full flex items-center gap-2 px-3 py-2 text-xs text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
                                    <X className="h-3 w-3" /> Clear selection
                                  </button>
                                )}
                                {filteredBrandKits.map(bk => (
                                  <button key={bk.id}
                                    onClick={() => { setSelectedBrandKitId(bk.id); setBrandKitOpen(false); setBrandKitSearch('') }}
                                    className={`w-full flex items-center gap-2 px-3 py-2 text-xs transition-colors ${
                                      selectedBrandKitId === bk.id
                                        ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-200'
                                        : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800'
                                    }`}
                                  >
                                    <span className={`w-5 h-5 rounded flex items-center justify-center text-[10px] font-bold flex-shrink-0 ${
                                      selectedBrandKitId === bk.id
                                        ? 'bg-primary-600 text-white'
                                        : 'bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-400'
                                    }`}>
                                      {bk.name.charAt(0).toUpperCase()}
                                    </span>
                                    {bk.name}
                                  </button>
                                ))}
                                {filteredBrandKits.length === 0 && (
                                  <p className="px-3 py-3 text-xs text-gray-500">No brand kits found</p>
                                )}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between gap-3 px-8 py-3 border-t border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950 flex-shrink-0">
                  <div className="min-w-0 text-xs text-gray-600 dark:text-gray-400 flex items-center gap-3 flex-wrap">
                    <span>
                      <span className="font-medium text-gray-900 dark:text-white">{selectedSizes.size}</span> size{selectedSizes.size === 1 ? '' : 's'}
                    </span>
                    <span className="text-gray-300 dark:text-gray-700">&bull;</span>
                    <span>
                      {selectedBrandKit ? (
                        <span className="font-medium text-gray-900 dark:text-white">{selectedBrandKit.name}</span>
                      ) : 'no brand kit'}
                    </span>
                    {referenceImages.length > 0 && (
                      <>
                        <span className="text-gray-300 dark:text-gray-700">&bull;</span>
                        <span>{referenceImages.length} reference{referenceImages.length === 1 ? '' : 's'}</span>
                      </>
                    )}
                    {hintMessage && (
                      <>
                        <span className="text-gray-300 dark:text-gray-700">&bull;</span>
                        <span className="text-amber-600 dark:text-amber-400">{hintMessage}</span>
                      </>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={onClose}
                      className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => {
                        // Surface inline errors for any required field
                        // that's still missing when the user clicks the
                        // (visually) disabled CTA. Button stays clickable
                        // so this handler can do the surfacing + scroll.
                        const sizesMissing = selectedSizes.size === 0
                        const urlInvalid = !isDestinationUrlValid

                        if (urlInvalid) setDestinationUrlTouched(true)
                        if (sizesMissing) setSizesTouched(true)

                        // Scroll to the topmost invalid field. The right
                        // column lays out URL → Sizes → Brand Kit, so URL
                        // wins if both are wrong.
                        if (urlInvalid) {
                          destinationUrlRef.current?.scrollIntoView({
                            behavior: 'smooth',
                            block: 'center',
                          })
                          destinationUrlRef.current?.focus({ preventScroll: true })
                          return
                        }
                        if (sizesMissing) {
                          sizesPanelRef.current?.scrollIntoView({
                            behavior: 'smooth',
                            block: 'center',
                          })
                          return
                        }
                        if (!canSubmit) return
                        handleSubmit()
                      }}
                      aria-disabled={!canSubmit}
                      className={`inline-flex items-center gap-2 px-5 py-2 text-sm font-semibold rounded-lg transition-colors ${
                        canSubmit
                          ? 'bg-primary-600 hover:bg-primary-500 text-white'
                          : 'bg-primary-600/50 text-white cursor-not-allowed'
                      }`}
                    >
                      <Sparkles className="h-3.5 w-3.5" />
                      Generate Creative
                    </button>
                  </div>
                </div>
              </>
            )}

            {/* ── Phase 2: Generating (loading screen) ── */}
            {phase === 2 && (
              <div className="flex-1 min-h-0 flex flex-col">
                <div className="flex-1 flex flex-col items-center justify-center gap-5 px-8 py-10">
                  <div className="w-14 h-14 rounded-2xl bg-primary-100 dark:bg-primary-900/30 border border-primary-200 dark:border-primary-800 flex items-center justify-center">
                    <Loader2 className="h-7 w-7 text-primary-600 dark:text-primary-400 animate-spin" />
                  </div>
                  <div className="text-center max-w-sm">
                    <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-1.5">
                      Generating your creative
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                      You can follow progress and answer any follow-up
                      questions in the chat on the left. Feel free to close
                      this dialog any time &mdash; we&rsquo;ll notify you in
                      the chat as soon as generation is complete.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

interface SizesPanelProps {
  sizes: Set<string>
  onToggle: (size: string) => void
  onRemove: (size: string) => void
  onAdd: (size: string) => void
  onAddMany: (sizes: string[]) => void
  onClearAll: () => void
  /**
   * Set by the parent when the user attempted to submit without picking a
   * size. Drives the red border + inline error message so the panel
   * matches the Landing Page URL field's UX.
   */
  showRequiredError?: boolean
}

function SizesPanel({ sizes, onToggle, onRemove, onAdd, onAddMany, onClearAll, showRequiredError = false }: SizesPanelProps) {
  const [query, setQuery] = useState('')
  const [customW, setCustomW] = useState('')
  const [customH, setCustomH] = useState('')
  const [error, setError] = useState('')

  const dragModeRef = useRef<null | 'add' | 'remove'>(null)
  const [isDragging, setIsDragging] = useState(false)

  useEffect(() => {
    const endDrag = () => {
      if (dragModeRef.current !== null) {
        dragModeRef.current = null
        setIsDragging(false)
      }
    }
    window.addEventListener('mouseup', endDrag)
    window.addEventListener('mouseleave', endDrag)
    return () => {
      window.removeEventListener('mouseup', endDrag)
      window.removeEventListener('mouseleave', endDrag)
    }
  }, [])

  const q = query.trim().toLowerCase()

  const filteredPresets = useMemo(() => {
    if (!q) return PRESET_SIZES
    return PRESET_SIZES.filter(p =>
      p.size.toLowerCase().includes(q) ||
      p.label.toLowerCase().includes(q) ||
      p.category.toLowerCase().includes(q)
    )
  }, [q])

  const allFilteredSelected =
    filteredPresets.length > 0 && filteredPresets.every(p => sizes.has(p.size))

  const handleAddCustom = () => {
    const w = parseInt(customW, 10)
    const h = parseInt(customH, 10)
    if (!w || !h || w <= 0 || h <= 0 || w > 9999 || h > 9999) {
      setError('Enter valid width and height (1-9999)')
      return
    }
    const size = `${w}x${h}`
    if (sizes.has(size)) { setError('Size already added'); return }
    onAdd(size)
    setCustomW('')
    setCustomH('')
    setError('')
  }

  const handleCustomKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') { e.preventDefault(); handleAddCustom() }
  }

  const handleDragStart = (size: string) => {
    if (sizes.has(size)) {
      dragModeRef.current = 'remove'
      onRemove(size)
    } else {
      dragModeRef.current = 'add'
      onAdd(size)
    }
    setIsDragging(true)
  }

  const handleDragOver = (size: string) => {
    const mode = dragModeRef.current
    if (!mode) return
    if (mode === 'add' && !sizes.has(size)) onAdd(size)
    else if (mode === 'remove' && sizes.has(size)) onRemove(size)
  }

  const selectedArray = Array.from(sizes)

  // Active when the user has attempted submit without picking any size.
  const showInvalidState = showRequiredError && sizes.size === 0

  return (
    <div
      className={`rounded-xl border bg-gray-50 dark:bg-gray-900/40 overflow-hidden transition-colors ${
        showInvalidState
          ? 'border-red-400 dark:border-red-500/70'
          : 'border-gray-200 dark:border-gray-800'
      }`}
    >
      {/* Header */}
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-gray-200 dark:border-gray-800 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-baseline gap-2">
            <h3 className="text-sm font-semibold">
              Sizes <span className="text-red-500">*</span>
            </h3>
            {sizes.size > 0 && (
              <span className="text-[11px] font-medium text-primary-600 dark:text-primary-400">
                {sizes.size} selected
              </span>
            )}
            {showInvalidState && (
              <span className="text-[11px] font-medium text-red-500 dark:text-red-400">
                Pick at least one size
              </span>
            )}
          </div>
          <div className="flex items-center gap-1 text-[11px]">
            <button
              type="button"
              onClick={() => {
                if (allFilteredSelected) filteredPresets.forEach(p => onRemove(p.size))
                else onAddMany(filteredPresets.map(p => p.size))
              }}
              disabled={filteredPresets.length === 0}
              className="px-2 py-1 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {allFilteredSelected ? 'Unselect all' : q ? `Select all (${filteredPresets.length})` : 'Select all'}
            </button>
            <span className="text-gray-300 dark:text-gray-700">|</span>
            <button
              type="button"
              onClick={onClearAll}
              disabled={sizes.size === 0}
              className="px-2 py-1 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Clear
            </button>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 px-1 py-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg focus-within:border-primary-500 focus-within:ring-2 focus-within:ring-primary-500/40">
            <input
              type="number"
              min={1}
              max={9999}
              value={customW}
              onChange={e => { setCustomW(e.target.value); setError('') }}
              onKeyDown={handleCustomKey}
              placeholder="W"
              className="w-12 bg-transparent text-xs text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 outline-none px-1.5 py-1 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
            />
            <span className="text-gray-400 dark:text-gray-600 text-xs">&times;</span>
            <input
              type="number"
              min={1}
              max={9999}
              value={customH}
              onChange={e => { setCustomH(e.target.value); setError('') }}
              onKeyDown={handleCustomKey}
              placeholder="H"
              className="w-12 bg-transparent text-xs text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 outline-none px-1.5 py-1 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
            />
          </div>
          <button
            type="button"
            onClick={handleAddCustom}
            disabled={!customW || !customH}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold rounded-lg bg-primary-600 hover:bg-primary-500 text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Plus className="h-3 w-3" />
            Add
          </button>
        </div>
      </div>

      {error && <div className="px-4 pt-2 text-[11px] text-red-500 dark:text-red-400">{error}</div>}

      {/* Search + chips */}
      <div className="px-4 pt-3 space-y-2.5">
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900/60 focus-within:border-primary-500 focus-within:ring-2 focus-within:ring-primary-500/40">
          <Search className="h-3.5 w-3.5 text-gray-500 flex-shrink-0" />
          <input
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search sizes (e.g. 300, leaderboard, mobile)"
            className="flex-1 min-w-0 bg-transparent text-sm text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 outline-none"
          />
          {query && (
            <button type="button" onClick={() => setQuery('')} className="text-gray-500 hover:text-gray-900 dark:hover:text-white">
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        {selectedArray.length > 0 && (
          <div className="max-h-20 overflow-y-auto scrollbar-thin">
            <div className="flex flex-wrap gap-1.5">
              {selectedArray.map(size => {
                const preset = getPresetBySize(size)
                return (
                  <span key={size} className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-md bg-primary-600 text-white">
                    <span className="font-mono">{size}</span>
                    {preset && <span className="text-primary-100/80 text-[10px]">&middot; {preset.label}</span>}
                    <button type="button" onClick={() => onRemove(size)} className="hover:bg-primary-700 rounded">
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                )
              })}
            </div>
          </div>
        )}
      </div>

      {/* Visual grid */}
      <div className="px-4 py-3">
        {filteredPresets.length === 0 ? (
          <div className="px-3 py-6 text-center text-xs text-gray-500">
            No presets match &quot;{query}&quot;. Add it as a custom size above.
          </div>
        ) : (
          <>
            <div
              className={`grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-[260px] overflow-y-auto scrollbar-thin pr-1 ${
                isDragging ? 'select-none cursor-crosshair' : ''
              }`}
            >
              {filteredPresets.map(preset => (
                <SizeCard
                  key={preset.size}
                  preset={preset}
                  active={sizes.has(preset.size)}
                  onDragStart={() => handleDragStart(preset.size)}
                  onDragOver={() => handleDragOver(preset.size)}
                  onKeyToggle={() => onToggle(preset.size)}
                />
              ))}
            </div>
            <div className="mt-2 text-[11px] text-gray-500">
              Tip: click and drag across cards to select multiple at once.
            </div>
          </>
        )}
      </div>
    </div>
  )
}

interface SizeCardProps {
  preset: SizePreset
  active: boolean
  onDragStart: () => void
  onDragOver: () => void
  onKeyToggle: () => void
}

function SizeCard({ preset, active, onDragStart, onDragOver, onKeyToggle }: SizeCardProps) {
  const maxDim = 48
  const ratio = preset.w / preset.h
  const boxW = ratio >= 1 ? maxDim : Math.max(10, Math.round(maxDim * ratio))
  const boxH = ratio >= 1 ? Math.max(10, Math.round(maxDim / ratio)) : maxDim

  return (
    <div
      role="button"
      tabIndex={0}
      aria-pressed={active}
      onMouseDown={e => { e.preventDefault(); onDragStart() }}
      onMouseEnter={() => onDragOver()}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onKeyToggle()
        }
      }}
      className={`group relative flex flex-col items-center justify-between gap-1 p-2 rounded-lg border transition-all duration-150 cursor-pointer select-none focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500/60 ${
        active
          ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30 ring-2 ring-inset ring-primary-500/60'
          : 'border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900/40 hover:bg-gray-50 dark:hover:bg-gray-800/60 hover:border-gray-300 dark:hover:border-gray-700'
      }`}
    >
      {active && (
        <div className="absolute top-1 right-1 h-3.5 w-3.5 rounded-full bg-primary-600 flex items-center justify-center shadow">
          <Check className="h-2 w-2 text-white" />
        </div>
      )}
      <div className="h-12 w-full flex items-center justify-center pointer-events-none">
        <div
          className={`rounded-sm border ${
            active ? 'border-primary-400 bg-primary-500/40' : 'border-gray-300 dark:border-gray-600 bg-gray-200 dark:bg-gray-700 group-hover:bg-gray-300 dark:group-hover:bg-gray-600'
          }`}
          style={{ width: boxW, height: boxH }}
        />
      </div>
      <div className="w-full text-center pointer-events-none">
        <div className={`text-[11px] font-mono font-semibold ${active ? 'text-primary-700 dark:text-primary-100' : 'text-gray-900 dark:text-white'}`}>
          {preset.size}
        </div>
        <div className="text-[10px] text-gray-500 truncate">{preset.label}</div>
      </div>
    </div>
  )
}
